/**
 * Calendar Utilities
 * Helper functions for adding events to user's calendar
 */

/**
 * Format date for ICS file (iCalendar format)
 * @param {Date} date - JavaScript Date object
 * @returns {string} - Formatted date string (YYYYMMDDTHHMMSSZ)
 */
const formatICSDate = (date) => {
  const pad = (num) => String(num).padStart(2, '0');
  
  return `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`;
};


/**
 * Turn an event's wall-clock date/time into a real instant.
 *
 * `new Date('2026-08-31')` parses as UTC midnight, so setHours() afterwards
 * lands on the previous day for anyone west of UTC — that is why exported
 * events arrived a day early. This resolves the time in the EVENT's timezone
 * (falling back to the viewer's) so exports are correct for everyone.
 */
const getEventStart = (event_date, event_time, eventTimezone) => {
  const [year, month, day] = event_date.split('-').map(Number);
  const [hours, minutes] = (event_time || '00:00').split(':').map(Number);

  if (!eventTimezone) {
    return new Date(year, month - 1, day, hours, minutes, 0);
  }

  const asUTC = Date.UTC(year, month - 1, day, hours, minutes, 0);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: eventTimezone, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(asUTC)).reduce((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});

  const asZoned = Date.UTC(
    Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    Number(parts.hour) % 24, Number(parts.minute), Number(parts.second)
  );

  return new Date(asUTC + (asUTC - asZoned));
};

const getEventWindow = (event) => {
  // Bookings already hold a true UTC instant; calendar events hold wall-clock + zone
  const start = event.start_utc
    ? new Date(event.start_utc)
    : getEventStart(event.event_date, event.event_time, event.timezone);
  const end = new Date(start.getTime() + (event.duration_minutes || 60) * 60 * 1000);
  return { start, end };
};

/**
 * Generate ICS file content for calendar event
 * @param {Object} event - Event object with title, description, date, time, link
 * @returns {string} - ICS file content
 */
export const generateICS = (event) => {
  const { title, description, link } = event;
  const { start: eventDate, end: endDate } = getEventWindow(event);
  
  // Clean description (remove "Join URL:" part if present)
  const cleanDescription = description?.split('\n\nJoin URL:')[0] || '';
  
  // Add join link to description if available
  const fullDescription = link 
    ? `${cleanDescription}\n\nJoin: ${link}`.trim()
    : cleanDescription;
  
  // Generate unique ID
  const uid = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}@11-11-collective`;
  
  // Create ICS content
  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//11/11 Collective Connection//Calendar Events//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${formatICSDate(new Date())}`,
    `DTSTART:${formatICSDate(eventDate)}`,
    `DTEND:${formatICSDate(endDate)}`,
    `SUMMARY:${title}`,
    `DESCRIPTION:${fullDescription.replace(/\n/g, '\\n')}`,
    // A street address belongs in LOCATION, not only buried in the description
    (event.location || link) ? `LOCATION:${(event.location || link).replace(/\n/g, ', ')}` : '',
    link ? `URL:${link}` : '',
    'STATUS:CONFIRMED',
    'SEQUENCE:0',
    'END:VEVENT',
    'END:VCALENDAR'
  ].filter(line => line !== '').join('\r\n');
  
  return icsContent;
};

/**
 * Download ICS file
 * @param {Object} event - Event object
 */
export const downloadICS = (event) => {
  const icsContent = generateICS(event);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${event.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

/**
 * Generate Google Calendar URL
 * @param {Object} event - Event object
 * @returns {string} - Google Calendar URL
 */
export const getGoogleCalendarUrl = (event) => {
  const { title, description, link } = event;
  const { start: eventDate, end: endDate } = getEventWindow(event);
  
  // Format dates for Google Calendar
  const formatGoogleDate = (date) => {
    return date.toISOString().replace(/-|:|\.\d+/g, '');
  };
  
  // Google's event editor ignores the trailing Z and reads the value as local
  // wall-clock time. Passing wall-clock + ctz is the unambiguous form it honours.
  const wallClockInTz = (date, tz) => {
    const p = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).formatToParts(date).reduce((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});
    const hh = String(Number(p.hour) % 24).padStart(2, '0');
    return `${p.year}${p.month}${p.day}T${hh}${p.minute}${p.second}`;
  };

  const startTime = event.timezone ? wallClockInTz(eventDate, event.timezone) : formatGoogleDate(eventDate);
  const endTime = event.timezone ? wallClockInTz(endDate, event.timezone) : formatGoogleDate(endDate);
  
  // Clean description
  const cleanDescription = description?.split('\n\nJoin URL:')[0] || '';
  const fullDescription = link 
    ? `${cleanDescription}\n\nJoin: ${link}`
    : cleanDescription;
  
  // Build Google Calendar URL
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${startTime}/${endTime}`,
    details: fullDescription,
    location: event.location || link || '',
    ...(event.timezone ? { ctz: event.timezone } : {}),
  });
  
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/**
 * Generate Outlook Calendar URL
 * @param {Object} event - Event object
 * @returns {string} - Outlook Calendar URL
 */
export const getOutlookCalendarUrl = (event) => {
  const { title, description, link } = event;
  const { start: eventDate, end: endDate } = getEventWindow(event);
  
  // Format dates for Outlook (ISO 8601)
  const startTime = eventDate.toISOString();
  const endTime = endDate.toISOString();
  
  // Clean description
  const cleanDescription = description?.split('\n\nJoin URL:')[0] || '';
  const fullDescription = link 
    ? `${cleanDescription}\n\nJoin: ${link}`
    : cleanDescription;
  
  // Build Outlook Calendar URL
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: title,
    startdt: startTime,
    enddt: endTime,
    body: fullDescription,
    location: event.location || link || ''
  });
  
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

/**
 * Generate Apple Calendar (iCal) data URI
 * @param {Object} event - Event object
 * @returns {string} - Data URI for .ics file
 */
export const getAppleCalendarDataUri = (event) => {
  const icsContent = generateICS(event);
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`;
};
